import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Search } from "lucide-react";
import { api } from "../../lib/api";
import { formatCurrency } from "../../lib/utils";
import { Input } from "../../components/ui/input";

export default function HospitalDetail() {
  const { id } = useParams();
  const [hospital, setHospital] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/patient/hospitals/${id}`),
      api.get(`/patient/hospitals/${id}/doctors`),
    ]).then(([h, d]) => { setHospital(h.data); setDoctors(d.data); })
      .finally(() => setLoading(false));
  }, [id]);

  const filtered = q.trim()
    ? doctors.filter(d => [d.full_name, d.specialty].filter(Boolean).some(v => v.toLowerCase().includes(q.toLowerCase())))
    : doctors;

  return (
    <div className="space-y-8" data-testid="hospital-detail-page">
      <Link to="/patient/hospitals" className="text-sm text-mb-fg2 hover:text-white inline-flex items-center gap-2">
        <ArrowLeft className="h-4 w-4" /> All hospitals
      </Link>
      {loading ? (
        <div className="text-sm text-mb-fg2">Loading…</div>
      ) : hospital ? (
        <>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ hospital</div>
            <div className="mt-2 flex items-start gap-4">
              {hospital.logo_url && (
                <img src={hospital.logo_url} alt="" data-testid="hospital-detail-logo"
                  className="h-14 w-14 rounded-lg object-contain bg-white/5 p-1.5 border border-mb-border shrink-0" />
              )}
              <div>
                <h1 className="text-3xl sm:text-4xl tracking-tight font-light" data-testid="hospital-detail-name">
                  {hospital.name}
                </h1>
                {hospital.address && (
                  <div className="mt-2 text-sm text-mb-fg2 inline-flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> {hospital.address}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-mb-fg2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="hospital-doctors-search"
              placeholder="Search by name or specialty"
              className="pl-9 bg-mb-surface2 border-mb-border text-white h-10 rounded-lg focus-visible:ring-teal-500" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.length === 0 && <div className="text-sm text-mb-fg2">No doctors match.</div>}
            {filtered.map(d => (
              <Link key={d.id} to={`/patient/doctors/${d.id}`}
                data-testid={`doctor-card-${d.id}`}
                className="group rounded-xl border border-mb-border bg-mb-surface p-5 hover:border-teal-500/30 transition-colors block">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-teal-500/10 text-teal-300 flex items-center justify-center font-medium">
                    {(d.full_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0">
                    <div className="text-base text-white truncate">{d.full_name}</div>
                    <div className="text-xs text-mb-fg2 truncate">{d.specialty}</div>
                  </div>
                </div>
                <div className="mt-4 text-sm text-mb-fg2 flex justify-between">
                  <span>{formatCurrency(d.consultation_fee)}</span>
                  <span className="text-teal-400">{d.slot_duration_minutes} min slots</span>
                </div>
              </Link>
            ))}
          </div>
        </>
      ) : <div>Hospital not found.</div>}
    </div>
  );
}
